import React, {useContext} from "react";
import {useAsync2} from "nate-react-api-helpers";
import {useNavigate} from "react-router-dom";
import {SelectEditor} from "../../../../misc/scroller/SelectEditor";
import {EditContext} from "../../../../misc/scroller/EditProvider";
import {api} from "../../../../api/API";
import {useProjectId} from "../ProjectName";
import {useSnackAction} from "../hardware/SnackAction";
import {DropdownDataLoading} from "../../settings/DropDowns";
import {doorElevationPickerOptions, legacyPickerName, withLegacyDoorElevation} from "./DoorElevationHelpers";

// the in-cell picker shown for an opening's door elevation: an autocomplete of the
// project's existing door elevations, the global ones, and the file-based (v1) names. Selecting one links this opening to that door (see
// drw.AdoptDoorElevationHandler — no identity change, unlike the screen picker), then
// reloads the row. Typing a name and picking the free-solo entry instead unlinks the
// opening and opens it in the elevation tool to draw a fresh one.
export function PickDoorElevation(props: {
    anchor: any;
    row: {id: number};
    width: number;
    onCancel(): void;
}) {
    const project = useProjectId();
    const editCtx = useContext(EditContext);
    const navigate = useNavigate();

    // scope the list to this opening so the server hides doors of an incompatible opening type.
    const list = useAsync2((p: number) => api.elevationTool.listDoorElevations({project: p, opening: props.row.id}), project, [project]);

    const adopt = useSnackAction(async (door: number) => {
        await api.elevationTool.adoptDoor({project, opening: props.row.id, door});
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Door elevation linked"});

    // free-solo: unlink this opening's door elevation, then open the tool, passing the typed
    // name so it auto-saves a fresh door elevation under it.
    const drawNew = useSnackAction(async (name: string) => {
        await api.elevationTool.unlinkDoor({project, opening: props.row.id});
        navigate(`/project/${project}/shop-drawing/elevation-tool?opening=${props.row.id}&workspace=door&autoSaveName=${encodeURIComponent(name.trim())}`);
    }, {success: "Unlinked — opening elevation tool"});

    // a v1 name unlinks the drw door and sets the legacy columns in the same request.
    const pickLegacy = useSnackAction(async (name: string) => {
        const [row] = await api.openings.getByIds({project, ids: [props.row.id]});
        await api.elevationTool.unlinkDoor({project, opening: props.row.id, legacyOpening: withLegacyDoorElevation(row, name)});
        await editCtx.reload(true, [props.row.id]);
        props.onCancel();
    }, {success: "Door elevation set"});

    if(list.loading) return <DropdownDataLoading anchor={props.anchor} width={props.width} />;

    const options = doorElevationPickerOptions(list.asList);

    const select = (value: string, freeSolo: boolean) => {
        if(freeSolo) return drawNew.callback(value);

        const legacy = legacyPickerName(value);
        if(legacy !== null) return pickLegacy.callback(legacy);

        return adopt.callback(parseInt(value));
    };

    return (
        <SelectEditor
            anchor={props.anchor}
            options={options}
            value=""
            initialValue=""
            freeSolo
            onBlur={props.onCancel}
            onSelect={select}
        />
    );
}
